import * as React from 'react';
import { Column, StatisticsFilters } from './types';
import { Link } from 'react-router-dom';
import Title from 'src/components/Title';
import Breadcrumb from 'src/components/Breadcrumb';
import BreadcrumbItem from 'src/components/Breadcrumb/BreadcrumbItem';
import Filters from './components/Filters';
import ResultTable from './components/ResultTable';
import { pluralize, pluralizeWithCount } from 'src/utils/stringUtils';
import Select from 'src/components/FormElements/Select';
import Button from 'src/components/Button';
import Icon from 'src/components/Icon';
import ColumnSelect from './components/ColumnSelect';
import SendForm from './components/SendForm';
import { addToast, ToastContainer } from 'src/components/Toast';
import { getStatisticResult, queueStatisticReport } from 'src/helpers/api';
import { IGetStatisticResultOption, IQueueStatisticReportOptions, IStatisticsItem } from 'src/types/Statistics';
import { formatDate, getDateRange } from 'src/utils/dateUtils';
import Pagination from 'src/components/Pagination';
import classNames from 'classnames';
import Badge from 'src/components/Badge';

const Statistics = () => {
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);
  const [columns, setColumns] = React.useState<Column[]>(['completed']);
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [totalResultSize, setTotalResultSize] = React.useState<number>(0);
  const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [requestError, setRequestError] = React.useState<Error>();
  const [sortOrder, setSortOrder] = React.useState<string | undefined>(undefined);
  const [items, setItems] = React.useState<IStatisticsItem[]>([]);
  const [filters, setFilters] = React.useState<StatisticsFilters>({
    period: {
      type: 'month',
      from: null,
      to: null
    },
    activityNameQuery: '',
    activityNumberQuery: null,
    organizers: [],
    themes: [],
    statuses: [],
    type: 'default',
  });
  const [sendFormType, setSendFormType] = React.useState<'pdf' | 'excel' | null>(null);

  const handleSortOrderChange = React.useCallback((event : React.ChangeEvent<HTMLSelectElement>) => {
      setSortOrder(event.target.value);
  }, [])

  const search = React.useCallback(async () => {
    const data: IGetStatisticResultOption = {
      pageNumber,
      pageSize: 25,
      sortOrder
    };

    if (filters.type === 'advertisement') {
      data.isAdvertisement = 1;
    }
    if (filters.organizers.length > 0){
      data.departmentIds = filters.organizers;
    }
    if (filters.statuses.length > 0){
      data.courseStatusCode = filters.statuses;
    }
    if (filters.themes.length > 0){
      data.themeIds = filters.themes;
    }

    if (filters.period.type !== 'custom') {
      const { from, to } = getDateRange(filters.period.type);

      data.startDate = formatDate(from, 'yyyy-MM-dd');
      data.endDate = formatDate(to, 'yyyy-MM-dd');
    } else if (filters.period.from || filters.period.to) {
      data.startDate = filters.period.from ?  formatDate(filters.period.from, 'yyyy-MM-dd') : undefined;
      data.endDate = filters.period.to ?  formatDate(filters.period.to, 'yyyy-MM-dd') : undefined;
    }

    if(filters.activityNumberQuery){
      data.courseCode = filters.activityNumberQuery;
    }

    if(filters.activityNameQuery){
      data.courseName = filters.activityNameQuery;
    }

    setIsLoading(true);

    try {
      const result = await getStatisticResult(data);
      setTotalResultSize(result.totalResults)
      setPageNumber(result.page);
      setItems(result.statisticResults);
      setIsLoaded(true);
    } catch (error) {
      setRequestError(error);
    } finally {
      setIsLoading(false);
    }
  }, [filters, pageNumber, sortOrder]);

  const scheduleSearch = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(search, 200);
  }, [filters, pageNumber, sortOrder]);

  React.useEffect(()=> {
    scheduleSearch();
  }, [filters,pageNumber, sortOrder]);

  const handleSend = async (emails: string[]) => {
    const data: IQueueStatisticReportOptions = {
      emails,
    };

    if (filters.type === 'advertisement') {
      data.isAdvertisement = 1;
    }
    if (filters.organizers.length > 0){
      data.departmentIds = filters.organizers;
    }
    if (filters.statuses.length > 0){
      data.courseStatusCode = filters.statuses;
    }
    if (filters.themes.length > 0){
      data.themeIds = filters.themes;
    }

    if (filters.period.type !== 'custom') {
      const { from, to } = getDateRange(filters.period.type);

      data.startDate = formatDate(from, 'yyyy-MM-dd');
      data.endDate = formatDate(to, 'yyyy-MM-dd');
    } else if (filters.period.from || filters.period.to) {
      data.startDate = filters.period.from ?  formatDate(filters.period.from, 'yyyy-MM-dd') : undefined;
      data.endDate = filters.period.to ?  formatDate(filters.period.to, 'yyyy-MM-dd') : undefined;
    }

    if(filters.activityNumberQuery){
      data.courseCode = filters.activityNumberQuery;
    }

    if(filters.activityNameQuery){
      data.courseName = filters.activityNameQuery;
    }

    await queueStatisticReport(data)

    addToast({
      message: `Rapporten bliver genereret og sendt til ${pluralize(emails.length, 'den indtastede e-mail', 'de indtastede e-mailadresser')}`,
      type: 'info',
      duration: 10000,
    });

    setSendFormType(null);
  };

  const toggleSendFormType = React.useCallback((type: 'pdf' | 'excel') => {
    if (type === sendFormType) {
      setSendFormType(null);
    } else {
      setSendFormType(type);
    }
  }, [sendFormType]);

  return (
    <div className="has-background-gray-body pb-14">
      <ToastContainer />
      <div className="wrapper">
        <Breadcrumb className="py3">
          <BreadcrumbItem>
            <Link to="/">Forside</Link>
          </BreadcrumbItem>
          <BreadcrumbItem active={true}>
            <Link to="">Statistik</Link>
          </BreadcrumbItem>
        </Breadcrumb>
        <Title className="mb3">Statistik</Title>
        <div className="has-background-white lg:flex p-8">
          <Filters
            className="w-full lg:w-64 xl:w-80 flex-none mr-8"
            filters={filters}
            onChange={setFilters}
          />
          <div className={classNames('w-full min-w-0', isLoading ? 'opacity-50' : '')}>
            <div className="flex items-center justify-between mb-5">
              <div className="f2 font-semibold">
                {pluralizeWithCount(totalResultSize, 'kursus', 'kurser')} fundet
              </div>
              <Select onChange={handleSortOrderChange}>
                <option value="-participants">Laveste antal deltager</option>
                <option value="participants">Højeste antal deltager</option>
                <option value="courseName">Navn (A-Z)</option>
                <option value="-courseName">Navn (Z-A)</option>
                <option value="departmentName">Arrangør (A-Z)</option>
                <option value="-departmentName">Arrangør (Z-A)</option>
                <option value="courseStatus">Status</option>
                <option value="theme">Tema</option>
              </Select>
            </div>
            <div className="has-background-gray-body mb-5 p-4 pt-6">
              <div className="flex items-end">
                <div className="relative mr-4">
                  <Button
                    disabled={true}
                    color={sendFormType === 'pdf' ? 'dark-blue' : 'primary'}
                    onClick={() => toggleSendFormType('pdf')}
                  >
                    <Icon name="pdf" className="-ml-1 mr-2" />
                    Send som PDF
                  </Button>
                  <div
                    className="leading-3 text-center f7 px-1 absolute uppercase font-semibold has-background-gray-800 has-text-white bottom-full"
                  >
                    Kommer snart
                  </div>
                </div>
                <Button color={sendFormType === 'excel' ? 'dark-blue' : 'primary'} onClick={() => toggleSendFormType('excel')}>
                  <Icon name="excel" className="-ml-1 mr-2" />
                  Send som Excel
                </Button>
                <ColumnSelect
                  className="ml-auto"
                  value={columns}
                  onChange={setColumns}
                />
              </div>
              <SendForm
                isExpanded={sendFormType !== null}
                onSubmit={handleSend}
              />
            </div>
            <ResultTable
              columns={columns}
              data={items}
            />
            {isLoaded && totalResultSize === 0 && (
              <div className="p-8 text-center font-bold">Ingen resultater</div>
            )}
            {totalResultSize > 0 && (
              <Pagination
                pageNumber={pageNumber}
                pageSize={25}
                totalResultSize={totalResultSize}
                onPageChange={setPageNumber}
                className="mt-6"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Statistics;



// WEBPACK FOOTER //
// ./src/Pages/CoursePortal/Reports/Statistics.tsx